import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import { usePageVisitTracking } from '../hooks/usePageVisitTracking';
import Container from '../components/common/Container';
import Button from '../components/common/Button';
import SectionHeader from '../components/common/SectionHeader';
import OpportunityGrid from '../components/features/dashboard/jobs/OpportunityGrid';
import OpportunityFilters from '../components/features/dashboard/jobs/OpportunityFilters';
import OpportunityDetailsModal from '../components/features/dashboard/jobs/OpportunityDetailsModal';
import ConnectionRequestModal from '../components/features/dashboard/jobs/ConnectionRequestModal';
import useStartupOpportunities from '../hooks/useStartupOpportunities';
import useOpportunityFilters from '../hooks/useOpportunityFilters';
import { BriefcaseIcon } from '@heroicons/react/24/outline';
import PageLayout from '../components/layout/PageLayout';

const JobsPage = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  
  // Track page visits for notifications
  usePageVisitTracking();

  // Filter state management
  const { 
    searchTerm, 
    filters, 
    handleSearchChange, 
    handleFilterChange,
    hasActiveFilters 
  } = useOpportunityFilters();

  // Opportunity data management
  const {
    opportunities,
    loading,
    error,
    hasMore,
    loadMore,
    refresh
  } = useStartupOpportunities(filters, searchTerm);



  // Handle retry for failed data fetches
  const handleRetryDataFetch = () => {
    refresh();
  };

  // Modal state management
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);

  // Connection request state management
  const [connectionRequestStatus, setConnectionRequestStatus] = useState(null); // 'submitting', 'success', 'error'
  const [connectionError, setConnectionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Auto-dismiss notifications after 5 seconds
  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  React.useEffect(() => {
    if (connectionError) {
      const timer = setTimeout(() => {
        setConnectionError(null);
      }, 8000); // Keep error messages longer
      return () => clearTimeout(timer);
    }
  }, [connectionError]);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Handle opportunity card click to show details
  const handleOpportunityClick = (opportunity) => {
    setSelectedOpportunity(opportunity);
    setIsDetailsModalOpen(true);
  };

  // Handle connect button click from opportunity card
  const handleConnectFromCard = (opportunity) => {
    // Reset connection state
    setConnectionRequestStatus(null);
    setConnectionError(null);
    setSuccessMessage(null);
    
    setSelectedOpportunity(opportunity);
    setIsConnectionModalOpen(true);
  };

  // Handle connect button click from details modal
  const handleConnectFromDetails = (opportunity) => {
    // Reset connection state
    setConnectionRequestStatus(null);
    setConnectionError(null);
    setSuccessMessage(null);
    
    setIsDetailsModalOpen(false);
    setSelectedOpportunity(opportunity);
    setIsConnectionModalOpen(true);
  };

  // Handle connection request success
  const handleConnectionSuccess = (connectionData) => {
    // Set success state
    setConnectionRequestStatus('success');
    setConnectionError(null);
    setSuccessMessage('Connection request submitted successfully! Our team will review it within 24-48 hours.');
    
    // Trigger refresh of opportunity cards to show updated connection status
    const refreshEvent = new CustomEvent('refreshOpportunityConnections', {
      detail: { opportunityId: selectedOpportunity?.id }
    });
    window.dispatchEvent(refreshEvent);
    
    // Close connection modal after a brief delay to show success state
    setTimeout(() => {
      setIsConnectionModalOpen(false);
      setSelectedOpportunity(null);
      setConnectionRequestStatus(null);
      setSuccessMessage(null);
    }, 2000);
    
    console.log('Connection request submitted successfully:', connectionData);
  };

  // Handle modal close
  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedOpportunity(null);
  };

  const handleCloseConnectionModal = () => {
    setIsConnectionModalOpen(false);
    setSelectedOpportunity(null);
    setConnectionRequestStatus(null);
    setConnectionError(null);
    setSuccessMessage(null);
  };

  // Handle connection request error
  const handleConnectionError = (error) => {
    setConnectionRequestStatus('error');
    setConnectionError(error || 'Failed to submit connection request. Please try again.');
    setSuccessMessage(null);
  };

  if (!user) {
    return (
      <div className="py-24 relative min-h-screen sm:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100 via-pink-300 to-purple-500 opacity-10 -z-10"></div>
        <Container variant="default">
          <div className="mx-auto max-w-2xl text-center">
            <div className="h-12 mb-8 flex justify-center">
              <svg width="156" height="48" viewBox="0 0 156 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M25.067 12.161C25.067 13.172 24.5 14.075 23.659 14.6C24.5 15.124 25.067 16.027 25.067 17.04V18.617C25.067 20.378 23.637 21.806 21.881 21.806H14.987V7.398H21.881C23.637 7.398 25.067 8.825 25.067 10.588V12.161ZM21.881 16.456C21.881 15.576 21.168 14.863 20.286 14.863H18.173V17.972H20.286C21.168 17.972 21.881 17.335 21.881 16.456ZM21.881 12.736C21.881 11.855 21.168 11.143 20.286 11.143H18.173V14.252H20.286C21.168 14.252 21.881 13.615 21.881 12.736ZM31.061 21.817H27.943V7.395H31.061V21.817ZM36.112 7.398H46.221V11.143H39.298V14.025H45.454V16.995H39.298V17.989H46.221V21.806H36.112V7.398ZM59.227 21.923C58.046 21.923 56.951 21.605 56.017 21.044L54.829 22.231L52.516 19.915L53.704 18.729C53.143 17.793 52.823 16.701 52.823 15.517C52.823 14.334 53.143 13.242 53.704 12.306L52.516 11.12L54.829 8.804L56.017 9.991C56.951 9.43 58.046 9.112 59.227 9.112C62.642 9.112 65.397 11.868 65.397 15.281C65.397 18.695 62.642 21.923 59.227 21.923ZM59.227 12.858C57.782 12.858 56.598 14.04 56.598 15.487C56.598 16.934 57.782 18.114 59.227 18.114C60.672 18.114 61.856 16.934 61.856 15.487C61.856 14.040 60.672 12.858 59.227 12.858ZM76.142 7.458V18.304C76.142 19.597 75.09 20.647 73.8 20.647H71.459C70.168 20.647 69.117 19.597 69.117 18.304V7.458H66V18.304C66 21.348 68.417 23.766 71.459 23.766H73.8C76.843 23.766 79.26 21.348 79.26 18.304V7.458H76.142ZM82.856 21.817H86.005V7.395H82.856V21.817ZM93.636 11.231H89.831V7.458H97.429C98.72 7.458 99.772 8.507 99.772 9.772V17.608C99.772 19.478 98.309 21.008 96.502 21.201L94.935 21.367V19.648C91.952 19.648 93.636 21.817 93.636 21.817H90.518V13.812C90.518 12.374 91.785 11.231 93.636 11.231ZM96.624 17.573V11.231H93.753V17.573H96.624ZM101.756 21.817H105.085V7.395H101.756V21.817ZM109.928 7.398H120.037V11.143H113.114V14.025H119.27V16.995H113.114V17.989H120.037V21.806H109.928V7.398ZM129.045 7.398H132.164V17.632H137.448V21.806H129.045V7.398Z" fill="#3a0ca3"/>
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">Please log in</h1>
            <p className="mt-4 text-lg text-gray-600">You need to be logged in to view this page.</p>
            <Button as="a" href="/" className="mt-8">Return to Homepage</Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <PageLayout>
      <div className="py-4 sm:py-6 lg:py-8">
        {/* Section Header */}
        <Container variant="default" className="mb-6">
          <SectionHeader 
            title="Job Opportunities"
            description="Discover flexible startup roles where you can shape your career through direct conversations with founders. These opportunities emphasize growth potential and collaborative role definition."
            icon={BriefcaseIcon}
          />
        </Container>

        {/* Debug button - only show in development */}


        {/* Search and Filters */}
        <OpportunityFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          filters={filters}
          onFilterChange={handleFilterChange}
          opportunities={opportunities}
          resultCount={opportunities.length}
          className="mb-6"
        />



        {/* Opportunities Grid */}
        <Container variant="default" className="mb-6">
          <OpportunityGrid
            opportunities={opportunities}
            loading={loading}
            error={error}
            onConnect={handleConnectFromCard}
            onClick={handleOpportunityClick}
            onLoadMore={loadMore}
            onRetry={handleRetryDataFetch}
            hasMore={hasMore}
          />
        </Container>

        {/* Opportunity Details Modal */}
        <OpportunityDetailsModal
          opportunity={selectedOpportunity}
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          onConnect={handleConnectFromDetails}
        />

        {/* Connection Request Modal */}
        <ConnectionRequestModal
          opportunity={selectedOpportunity}
          isOpen={isConnectionModalOpen}
          onClose={handleCloseConnectionModal}
          onSuccess={handleConnectionSuccess}
          onError={handleConnectionError}
        />

        {/* Success/Error Notifications */}
        {successMessage && (
          <div className="fixed top-4 right-4 z-50 max-w-md">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 
                           rounded-lg p-4 shadow-lg backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    Success!
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    {successMessage}
                  </p>
                </div>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="text-green-400 hover:text-green-600 dark:hover:text-green-300"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {connectionError && (
          <div className="fixed top-4 right-4 z-50 max-w-md">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 
                           rounded-lg p-4 shadow-lg backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    Error
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {connectionError}
                  </p>
                </div>
                <button
                  onClick={() => setConnectionError(null)}
                  className="text-red-400 hover:text-red-600 dark:hover:text-red-300"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default JobsPage;